// A Formik adapter to LabeledField

import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import LabeledField from '../LabeledField';

import formikPropTypes from './formikPropTypes';
import clearInputStatus from './clearInputStatus';

export function FormikLabeledField({
  as, children, disabled, error, label, name, onChange, placeholder, type, formikProps: {
    errors, handleChange, isSubmitting, touched, setStatus, status, values,
  },
}) {
  // any backend api/external error should be here
  // note, formik status is initialized as undefined
  const inputStatus = status?.[name];

  const handleInputChange = useCallback((e) => {
    onChange(e);
    handleChange(e);
    // clear any status messages associated with this input if they change it
    if (inputStatus) {
      setStatus(clearInputStatus(name));
    }
  }, [onChange, handleChange, setStatus, inputStatus, name]);

  return (
    <LabeledField
      as={as}
      id={name}
      input={{
        disabled: disabled || isSubmitting,
        name,
        onChange: handleInputChange,
        value: values[name],
      }}
      label={label}
      meta={{
        // custom error message || formik validation || backend api/external error
        error: error || errors[name] || inputStatus,
        touched: touched[name],
      }}
      placeholder={placeholder}
      type={type}
    >
      {children}
    </LabeledField>
  );
}

FormikLabeledField.propTypes = {
  /** HTML element (as a string) or component that will be rendered. */
  as: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.elementType,
  ]),
  /**
   * Array of elements that will be displayed as additional options if a
   * `<select/>` element is chosen for the `as` prop.
  */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /**
   * Manual disabled prop
   */
  disabled: PropTypes.bool,
  // specific error that overrides whatever is assumed with errors[name]
  error: PropTypes.string,
  /** Text that describes the content for the input element. */
  label: PropTypes.string.isRequired,
  /** Returns the name prop that you passed in. */
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  /** A hint to the user of what can be entered in the control. */
  placeholder: PropTypes.string,
  /** Value that will be used for the input's type attribute. */
  type: PropTypes.string,
  formikProps: formikPropTypes.isRequired,
};

FormikLabeledField.defaultProps = {
  as: 'input',
  children: null,
  disabled: false,
  error: null,
  onChange: () => {},
  placeholder: '',
  type: 'text',
};

export default FormikLabeledField;
